import React from "react";
import {
  Box,
  Button,
  Checkbox,
  FormControlLabel,
  Stack,
  Tooltip,
} from "@mui/material";
import OpenInNewIcon from "@mui/icons-material/OpenInNew";
import { SpecsSelectField } from "./SpecsEditorFields/index";
import SpecsInheritanceGroupDialog, {
  getSuggestedInheritanceGroups,
} from "./SpecsInheritanceGroupDialog";
import { LayerModel } from "./specsModel/LayerModel";
import { serializeSpecsNavHash } from "./specsNavHash";
import type { VersionModel } from "./specsModel/VersionModel";
import { useSpecsEditorContextValue } from "./useSpecsEditor";

const ALLOW_INHERITED_COLOR_TOOLTIP =
  "Permet aux layers de cet élément d'utiliser une couleur héritée.";
const INHERIT_FROM_GROUP_TOOLTIP =
  "Quand cet élément hérite, utilise le layer source assigné à ce groupe. Le layer par défaut utilise l'héritage normal.";
const EXCLUDE_FROM_INHERITANCE_TOOLTIP =
  "Empêche ce layer de recevoir une couleur héritée.";
const PARENT_INHERITANCE_GROUP_TOOLTIP =
  "Marque ce layer comme source possible pour ce groupe.";
const CREATE_INHERITANCE_GROUP_VALUE = "__create_inheritance_group__";
const checkboxLabelSx = {
  m: 0,
  gap: "6px",
  "& .MuiCheckbox-root": {
    p: 0,
  },
  "& .MuiFormControlLabel-label": {
    fontSize: "11px",
  },
};

type BoundLayer = NonNullable<ReturnType<VersionModel["getLayer"]>>;

function getInheritanceGroupOptions(inheritanceGroups: string[]) {
  return [
    { value: "", label: "Layer par défaut" },
    ...inheritanceGroups.map((groupName) => ({
      value: groupName,
      label: groupName,
    })),
    {
      value: CREATE_INHERITANCE_GROUP_VALUE,
      label: "Créer un nouveau groupe...",
    },
  ];
}

function getUniqueGroups(groups: Array<string | null | undefined>) {
  return Array.from(
    new Set(groups.filter((groupName): groupName is string => !!groupName))
  ).sort();
}

export default function SpecsSidebarInheritanceEditor() {
  const [createGroupTarget, setCreateGroupTarget] = React.useState<
    "element" | "layer" | null
  >(null);
  const [pendingInheritanceGroup, setPendingInheritanceGroup] =
    React.useState("");
  const {
    editor,
    selectedLayer: layer,
    currentVersion,
    updateLayerColor,
  } = useSpecsEditorContextValue();
  const boundLayer =
    layer?.modelLayerPath && currentVersion
      ? currentVersion.getLayer(layer.modelLayerPath)
      : null;

  if (!layer || !boundLayer || !currentVersion) {
    return null;
  }

  const layerDebugPath = boundLayer.layer._debugPath;
  const inheritanceGroups = currentVersion.getInheritanceGroups(
    boundLayer.part.name
  );
  const suggestedInheritanceGroups = getSuggestedInheritanceGroups(
    boundLayer.part
  );
  const elementInheritanceGroups = getUniqueGroups([
    ...inheritanceGroups,
    boundLayer.element.inheritFromGroup,
  ]);
  const parentInheritanceGroups = getUniqueGroups([
    ...inheritanceGroups,
    boundLayer.layer.parentOfInheritanceGroup,
  ]);
  const inheritanceGroupOptions = getInheritanceGroupOptions(
    elementInheritanceGroups
  );
  const parentInheritanceGroupOptions = [
    { value: "", label: "Aucun" },
    ...parentInheritanceGroups.map((groupName) => ({
      value: groupName,
      label: groupName,
    })),
    {
      value: CREATE_INHERITANCE_GROUP_VALUE,
      label: "Créer un nouveau groupe...",
    },
  ];
  const specsLayerNav = {
    kind: "layerSpecs",
    product: editor.identity.productName,
    version: editor.identity.productVersion,
    part: boundLayer.part.name,
    element: boundLayer.element.name,
    style: boundLayer.style.name,
    layerName: boundLayer.layer.name,
  } as const;
  const openCreateGroupDialog = (target: "element" | "layer") => {
    const firstNewGroup =
      suggestedInheritanceGroups.find(
        (groupName) => !inheritanceGroups.includes(groupName)
      ) ??
      suggestedInheritanceGroups[0] ??
      "";

    setPendingInheritanceGroup(firstNewGroup);
    setCreateGroupTarget(target);
  };

  const closeCreateGroupDialog = () => {
    setCreateGroupTarget(null);
    setPendingInheritanceGroup("");
  };

  const resetInheritedLayerColor = () => {
    if (LayerModel.isInheritedColor(layer.baseColor)) {
      updateLayerColor(layer.id, LayerModel.FALLBACK_PREVIEW_COLOR);
    }
  };

  const setElementInherit = (inherit: boolean) => {
    boundLayer.element.setInheritance({
      inherit,
      inheritFromGroup: inherit ? boundLayer.element.inheritFromGroup : "",
    });

    if (!inherit) {
      resetInheritedLayerColor();
    }
  };

  const setElementInheritFromGroup = (inheritFromGroup: string) => {
    if (inheritFromGroup === CREATE_INHERITANCE_GROUP_VALUE) {
      openCreateGroupDialog("element");
      return;
    }

    boundLayer.element.setInheritance({ inheritFromGroup });
  };

  const setLayerInheritanceMetadata = (patch: {
    parentOfInheritanceGroup?: string;
    excludeFromInheritance?: boolean;
    allowedInheritanceGroups?: string[];
  }) => {
    const { allowedInheritanceGroups, ...inheritanceMetadata } = patch;
    const updated = boundLayer.layer.setInheritanceMetadata(
      inheritanceMetadata,
      {
        inheritanceGroups: allowedInheritanceGroups ?? inheritanceGroups,
      }
    );

    if (updated && inheritanceMetadata.excludeFromInheritance) {
      resetInheritedLayerColor();
    }
  };

  const setParentOfInheritanceGroup = (parentOfInheritanceGroup: string) => {
    if (parentOfInheritanceGroup === CREATE_INHERITANCE_GROUP_VALUE) {
      openCreateGroupDialog("layer");
      return;
    }

    setLayerInheritanceMetadata({ parentOfInheritanceGroup });
  };

  const createInheritanceGroup = () => {
    if (!pendingInheritanceGroup || !createGroupTarget) {
      return;
    }

    if (createGroupTarget === "element") {
      setElementInheritFromGroup(pendingInheritanceGroup);
    } else {
      setLayerInheritanceMetadata({
        parentOfInheritanceGroup: pendingInheritanceGroup,
        allowedInheritanceGroups: getUniqueGroups([
          ...inheritanceGroups,
          pendingInheritanceGroup,
        ]),
      });
    }

    closeCreateGroupDialog();
  };

  return (
    <>
      <Stack spacing="16px" sx={{ mb: "10px", pt: "16px" }}>
        <Tooltip
          title={ALLOW_INHERITED_COLOR_TOOLTIP}
          placement="top"
          describeChild
        >
          <FormControlLabel
            control={
              <Checkbox
                size="small"
                checked={!!boundLayer.element.inherit}
                onChange={(_, inherit) => setElementInherit(inherit)}
              />
            }
            label="Permettre la couleur héritée"
            sx={checkboxLabelSx}
          />
        </Tooltip>

        {boundLayer.element.inherit ? (
          <Tooltip title={INHERIT_FROM_GROUP_TOOLTIP} placement="top">
            <Box>
              <SpecsSelectField
                label="Hériter du groupe"
                value={boundLayer.element.inheritFromGroup || ""}
                options={inheritanceGroupOptions}
                onChange={setElementInheritFromGroup}
              />
            </Box>
          </Tooltip>
        ) : null}

        <Tooltip
          title={EXCLUDE_FROM_INHERITANCE_TOOLTIP}
          placement="top"
          describeChild
        >
          <FormControlLabel
            control={
              <Checkbox
                size="small"
                checked={boundLayer.layer.excludeFromInheritance}
                onChange={(_, excludeFromInheritance) =>
                  setLayerInheritanceMetadata({
                    excludeFromInheritance,
                  })
                }
              />
            }
            label="Exclure de l'héritage"
            sx={checkboxLabelSx}
          />
        </Tooltip>

        <Tooltip title={PARENT_INHERITANCE_GROUP_TOOLTIP} placement="top">
          <Box>
            <SpecsSelectField
              label="Parent du groupe d'héritage"
              debugPath={
                layerDebugPath
                  ? `${layerDebugPath}.parentOfInheritanceGroup`
                  : undefined
              }
              value={boundLayer.layer.parentOfInheritanceGroup}
              onChange={setParentOfInheritanceGroup}
              options={parentInheritanceGroupOptions}
            />
          </Box>
        </Tooltip>

        <Button
          href={serializeSpecsNavHash(specsLayerNav)}
          size="small"
          variant="outlined"
          startIcon={<OpenInNewIcon fontSize="small" />}
          onClick={(event) => {
            if (event.metaKey || event.ctrlKey || event.shiftKey) {
              return;
            }

            event.preventDefault();
            editor.setNav(specsLayerNav);
          }}
        >
          Open layer specs
        </Button>
      </Stack>

      <SpecsInheritanceGroupDialog
        open={createGroupTarget !== null}
        part={boundLayer.part}
        value={pendingInheritanceGroup}
        onChange={setPendingInheritanceGroup}
        onClose={closeCreateGroupDialog}
        onCreate={createInheritanceGroup}
      />
    </>
  );
}
